// Portal v2 C6: /app/approvals/:id — the decision card. Cookie-authenticated
// (RECONCILIATION §6: emails deep-link here, never a tokenized one-click
// action): shows the workflow/run context, EXACTLY what runs next (the
// snapshotted final notification + template-resolved pending steps), how
// stale the snapshot is, and the Approve / Reject controls. The UUID alone
// grants nothing — the BFF answers a distinct 403 when the session email is
// outside the snapshotted approver set, honest 409s for decided/expired.
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, decideWorkflowRun, fetchApproval } from "../lib/api";
import { runChipTone } from "../components/WorkflowRuns";
import { useCan } from "../lib/session";
import type {
  ApprovalDetailResponse,
  ApprovalPendingStep,
  ApprovalStatus,
  ChipTone,
} from "../lib/types";

function ago(iso: string): string {
  const seconds = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

function untilLabel(iso: string): string {
  const seconds = (Date.parse(iso) - Date.now()) / 1000;
  if (Number.isNaN(seconds)) return "—";
  if (seconds <= 0) return "expired";
  if (seconds < 3600) return `in ${Math.max(1, Math.round(seconds / 60))}m`;
  if (seconds < 86400) return `in ${Math.round(seconds / 3600)}h`;
  return `in ${Math.round(seconds / 86400)}d`;
}

function shortWhen(iso: string | null): string {
  if (!iso) return "—";
  return iso.slice(0, 16).replace("T", " ");
}

function approvalChipTone(status: ApprovalStatus): ChipTone {
  switch (status) {
    case "pending":
      return "amber";
    case "approved":
      return "green";
    case "rejected":
    case "expired":
      return "red";
    default:
      return "gray";
  }
}

function PendingStep({ step }: { step: ApprovalPendingStep }) {
  const action = step.snapshotted_action;
  return (
    <div className="step-card">
      <div className="step-head">
        <b className="mono">{step.node_id}</b>
        <span className="chip gray">{step.node_type}</span>
        {step.unresolved.length > 0 ? (
          <span className="chip amber" title="These config keys reference steps that have not run yet; the raw template is shown.">
            unresolved: {step.unresolved.join(", ")}
          </span>
        ) : null}
      </div>
      {action ? (
        // The exact action snapshotted when the run parked — approve sends
        // THIS, never a re-render of mutable data.
        <div>
          <div className="result-meta">
            Exact snapshotted email — sent as-is on approve
          </div>
          <div style={{ margin: "6px 0" }}>
            <b>{String(action.subject ?? "(no subject)")}</b>
            <div className="wf-desc">to {(action.recipients ?? []).join(", ") || "—"}</div>
            {action.body_text ? (
              <pre className="json-pre" style={{ whiteSpace: "pre-wrap" }}>
                {String(action.body_text)}
              </pre>
            ) : null}
          </div>
        </div>
      ) : (
        <details className="json-details" open>
          <summary>Step config (template-resolved where possible)</summary>
          <pre className="json-pre">{JSON.stringify(step.config, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}

function decisionErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return `Not allowed: ${error.message}`;
    }
    if (error.status === 409) {
      return `No longer decidable: ${error.message}`;
    }
    return error.message;
  }
  return (error as Error)?.message ?? "decision failed";
}

function DecisionCard({ data }: { data: ApprovalDetailResponse }) {
  const canApprove = useCan("workflows.approve");
  const queryClient = useQueryClient();
  const [comment, setComment] = useState("");
  const { approval, run } = data;

  const decide = useMutation({
    mutationFn: (decision: "approve" | "reject") =>
      decideWorkflowRun(run.workflow_run_id, approval.workflow_run_approval_id, decision, comment),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["approval", approval.workflow_run_approval_id],
      });
      void queryClient.invalidateQueries({ queryKey: ["workflow-runs"] });
      void queryClient.invalidateQueries({ queryKey: ["home"] });
    },
    // 409/403 may mean someone else decided first — refetch the truth.
    onError: (error) => {
      if (error instanceof ApiError && (error.status === 409 || error.status === 404)) {
        void queryClient.invalidateQueries({
          queryKey: ["approval", approval.workflow_run_approval_id],
        });
      }
    },
  });

  const runLink = `/agents?tab=runs&run=${run.workflow_run_id}`;
  const decidable = approval.status === "pending" && !approval.is_expired;
  const effectiveStatus: ApprovalStatus =
    approval.status === "pending" && approval.is_expired ? "expired" : approval.status;

  return (
    <>
      <div className="pagehead">
        <h1>
          Approval — {run.definition_name} v{run.definition_version}{" "}
          <span className={`chip ${approvalChipTone(effectiveStatus)}`}>{effectiveStatus}</span>
        </h1>
        <div className="sub">
          run <Link to={runLink}>{run.workflow_run_id.slice(0, 8)}…</Link>{" "}
          <span className={`chip ${runChipTone(run.status)}`}>
            {run.status.replaceAll("_", " ")}
          </span>{" "}
          · trigger {run.trigger_reason ?? "—"} · requested by {run.triggered_by ?? "—"} · run
          created {shortWhen(run.created_at)}
        </div>
      </div>

      <div className="approval-banner">
        Action snapshot captured <b>{ago(data.snapshot_captured_at)} ago</b> (
        {shortWhen(data.snapshot_captured_at)}) — approving sends the snapshot below, not a
        re-render. Expires {untilLabel(approval.expires_at)}.
      </div>

      <section className="card">
        <h2>Decision</h2>
        <div className="wf-desc">{approval.summary}</div>
        <div className="result-meta">
          approvers {approval.approver_emails.join(", ") || "—"}
        </div>

        {approval.status !== "pending" ? (
          <div className="approval-banner" style={{ marginTop: 10 }}>
            <b>Decided:</b> {approval.status}
            {approval.decided_by ? ` by ${approval.decided_by}` : ""}
            {approval.decided_at ? ` at ${shortWhen(approval.decided_at)}` : ""}
            {approval.decision_comment ? ` — “${approval.decision_comment}”` : ""}.{" "}
            {approval.status === "approved" ? (
              <>
                The run resumed at the step after the gate.{" "}
                <Link to={runLink}>Watch it continue →</Link>
              </>
            ) : (
              <Link to={runLink}>View the run →</Link>
            )}
          </div>
        ) : approval.is_expired ? (
          <div className="wf-errors" style={{ marginTop: 10 }}>
            This approval expired before a decision — no action ran.{" "}
            <Link to={runLink}>View the run</Link>
          </div>
        ) : null}

        {decidable ? (
          <>
            <div style={{ margin: "12px 0 8px" }}>
              <textarea
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="Optional comment recorded with the decision"
                rows={2}
                style={{ width: "100%", boxSizing: "border-box" }}
                disabled={decide.isPending}
              />
            </div>
            {!canApprove ? (
              <div className="empty">
                Your session does not hold the workflows.approve capability, so the decision
                controls are disabled.
              </div>
            ) : null}
            <div className="filter-row" style={{ marginBottom: 0 }}>
              <button
                className="btn primary"
                type="button"
                disabled={!canApprove || decide.isPending}
                onClick={() => decide.mutate("approve")}
              >
                {decide.isPending ? "Deciding…" : "Approve — resume the run"}
              </button>
              <button
                className="btn"
                type="button"
                disabled={!canApprove || decide.isPending}
                onClick={() => decide.mutate("reject")}
              >
                Reject — cancel the run
              </button>
            </div>
          </>
        ) : null}
        {decide.isError ? (
          <div className="wf-errors" style={{ marginTop: 10 }}>
            {decisionErrorMessage(decide.error)}
          </div>
        ) : null}
      </section>

      <section className="card">
        <h2>What runs on approve</h2>
        {data.snapshot_error ? (
          <div className="wf-errors">
            Could not interpret the run snapshot: {data.snapshot_error}
          </div>
        ) : null}
        {data.pending_steps.length === 0 && !data.snapshot_error ? (
          <div className="empty">No steps remain after the approval gate.</div>
        ) : null}
        {data.pending_steps.map((step) => (
          <PendingStep key={step.node_id} step={step} />
        ))}
        {data.approval_node ? (
          <details className="json-details">
            <summary>Approval gate config ({data.approval_node.node_id})</summary>
            <pre className="json-pre">{JSON.stringify(data.approval_node.config, null, 2)}</pre>
          </details>
        ) : null}
      </section>
    </>
  );
}

export default function Approval() {
  const { id } = useParams<{ id: string }>();
  const detail = useQuery({
    queryKey: ["approval", id],
    queryFn: () => fetchApproval(id as string),
    enabled: Boolean(id),
    retry: false,
  });

  if (detail.isLoading) {
    return <div className="empty">Loading approval…</div>;
  }
  if (detail.isError || !detail.data) {
    const error = detail.error;
    if (error instanceof ApiError && error.status === 403) {
      return (
        <div className="card">
          <div className="empty">
            You cannot act on this approval: {error.message} Approvals are decided only by the
            people snapshotted on the approval itself.
          </div>
        </div>
      );
    }
    if (error instanceof ApiError && error.status === 404) {
      return (
        <div className="card">
          <div className="empty">This approval does not exist (or the link is stale).</div>
        </div>
      );
    }
    return (
      <div className="card">
        <div className="empty">Could not load the approval — {(error as Error)?.message}</div>
      </div>
    );
  }
  return <DecisionCard data={detail.data} />;
}
